import { useCallback, useState, type FunctionComponent } from 'react';
import { useSelector } from 'react-redux';

import { Gtm } from '@/core/tracking/utils/Gtm';
import { RadioSelect } from '@/domains/productDiscovery/Listings/components/Filters/components/RadioSelect/RadioSelect';
import { NB_FACETS_VISIBLE } from '@/productDiscovery/Listings/constants';
import { useFacet } from '@/productDiscovery/Listings/facets/hooks/useFacet';
import { useRefine } from '@/productDiscovery/Listings/hooks/useRefine';
import { selectCategoriesList } from '@/productDiscovery/Listings/selectors/categoriesList';
import { categoryLastFilterUrlKey } from '@/productDiscovery/Listings/services/algolia/filterConfig/filterConfigs';

import { FilterBlock } from '../../../FilterBlock';
import type { Item } from './interfaces';
import {
  ALL_CATEGORIES_FACET_VALUE,
  generateCategoriesRadioItems,
} from './utils/generateCategoriesRadioItems';

interface Props {
  className?: string;
  title: string;
  facetName: string;
  position?: number;
  filterCount?: number;
}

export const CategoriesRadioSelectFilter: FunctionComponent<Props> = ({
  facetName,
  title,
  className,
  filterCount,
  position,
}) => {
  const { facet } = useFacet(facetName);
  const refine = useRefine();
  const categoriesList = useSelector(selectCategoriesList);
  const [expanded, setExpanded] = useState(false);

  const handleChange = useCallback(
    async (_: unknown, selectedFacet: Item) => {
      if (selectedFacet) {
        const facetValue =
          selectedFacet.value === ALL_CATEGORIES_FACET_VALUE
            ? undefined
            : selectedFacet.value;

        refine(
          {
            [categoryLastFilterUrlKey]: facetValue,
            page: undefined,
          },
          undefined,
          true,
          {
            active: true,
            facetName,
            id: categoriesList[selectedFacet.value.toLowerCase()]?.id,
          },
        );

        const { generateFiltersTracking } = await import(
          '@/domains/productDiscovery/Listings/components/Filters/tracking/generateFiltersTracking'
        );

        Gtm.push(
          generateFiltersTracking({
            e_label: title,
            filter_id: facetName,
            filter_name: title,
            filter_value: selectedFacet.label,
            filter_position: position,
            filter_count: filterCount,
          }),
        );
      }
    },
    [facetName, filterCount, position, refine, title, categoriesList],
  );

  const toggleExpansion = () => {
    setExpanded(!expanded);
  };

  const hasValues = (facet?.values || []).length > 0;
  const isExpandable = facet && facet.values.length > NB_FACETS_VISIBLE;

  return (
    <FilterBlock
      id={facetName}
      title={title}
      className={className}
      disabled={!hasValues}
      expandable={isExpandable}
      expanded={expanded}
      onExpandedChange={toggleExpansion}
    >
      <RadioSelect
        items={generateCategoriesRadioItems(facet?.values, !expanded)}
        onChange={handleChange}
      />
    </FilterBlock>
  );
};

CategoriesRadioSelectFilter.displayName = 'CategoriesRadioSelectFilter';
